import camelize from "camelize";
import { quoteReference, isQuoteNumber } from "@/api/store/QuotesService.js";
import { getOrder, getOrderPaymentTypes } from "@/api/store/OrdersService.js";
import { getProducts } from "@/api/store/ProductsService.js";
import { selectBestLine } from "@/utils/product-line.js";

// Lo que comparten create_quote, update_quote y quote_shipping

// La clave del precio en las líneas de getProducts ("repair_shop" ->
// "repairShop"), igual que NewProductLine
export const priceKeyOf = order => camelize(order.priceLevel || "online");

// La línea de la pieza con el precio del nivel de la cotización, igual que la
// arma el buscador de productos de la orden (NewProductLine): el API valida el
// precio contra ese nivel. Sin línea vendible en la tienda devuelve { skipped }.
export const lineFor = async (nkrCode, quantity, priceKey, departmentCode) => {
  const res = await getProducts({
    storeEnabled: "enabled",
    page: 1,
    limitPerPage: "10",
    orderCol: "nkr_code",
    orderDir: "asc",
    search: nkrCode,
    requestPrice: true,
    priceLevel: "online",
    departmentCode
  });
  const product = res.products.find(p => p.nkrCode === nkrCode);
  if (!product) return { skipped: "no está en el catálogo del API" };
  // Los kits llevan sus piezas: se agregan desde la orden
  if (product.pack === "KIT") return { skipped: "es un kit" };
  const selectableLines = (product.pricesAndStock || []).filter(
    line =>
      line.canSell === true &&
      line.departamentoCodigo === departmentCode &&
      line.warehouse !== "FBA" &&
      line.line !== "OB"
  );
  if (!selectableLines.length) {
    return { skipped: "no se vende en esta tienda" };
  }
  const best = selectBestLine(selectableLines, priceKey);
  return {
    line: {
      productId: best.productId,
      nkrCode,
      line: best.line,
      description: product.customTitle || product.originalTitle,
      brand: product.brand,
      image: product.image,
      pack: product.pack,
      price: best[priceKey],
      quantity,
      warehouseId: best.warehouseId,
      warehouseCode: best.warehouse,
      departmentCode: best.departamentoCodigo,
      unificado: product.unificado || null,
      coreNkrCode: best.coreNkrCode || null,
      custom: false,
      isTransfer: false
    }
  };
};

export const emptyProductsUpdate = () => ({
  productsToAdd: [],
  productsToDelete: [],
  productsToUpdate: [],
  kitProductsToDelete: [],
  kitProductsToUpdate: [],
  kitProductsToAdd: []
});

// "Q-200000005", "200000005" o "-200000005" -> -200000005. Sólo
// cotizaciones: su número de orden es negativo (QuotesService).
const quoteOrderNumber = reference => {
  const digits = String(reference).replace(/\D/g, "");
  return digits ? -Number(digits) : null;
};

/**
 * La cotización a modificar, con los controles de toda edición del agente:
 * sólo cotizaciones (nunca órdenes), no convertidas en orden y creadas por
 * el usuario que lo usa. reference vacío = la de esta conversación.
 * Returns el order de getOrder.
 */
export const loadEditableQuote = async (reference, ctx) => {
  const selection = ctx.getSelection();
  const orderNumber = String(reference || "").trim()
    ? quoteOrderNumber(reference)
    : selection.quote && selection.quote.orderNumber;
  if (!orderNumber) {
    throw new Error(
      "No hay cotización: di cuál (Q-...) o crea una con create_quote."
    );
  }
  if (!isQuoteNumber(orderNumber)) {
    throw new Error("Sólo se pueden modificar cotizaciones, no órdenes.");
  }
  const order = await getOrder(orderNumber);
  if (!order || !order.isQuote) {
    throw new Error(`No existe la cotización ${quoteReference(orderNumber)}.`);
  }
  if (order.convertedOrderNumber) {
    throw new Error(
      `Esa cotización ya es la orden #${order.convertedOrderNumber}: no se modifica.`
    );
  }
  // Una sin creador (importada del módulo anterior) tampoco: no se sabe de
  // quién es
  if (
    !ctx.currentUserId ||
    String(order.createdByAdminId) !== String(ctx.currentUserId)
  ) {
    throw new Error(
      `La cotización ${quoteReference(
        orderNumber
      )} la creó otro usuario: sólo puedo modificar las tuyas. Puedes abrirla desde Cotizaciones o crear una nueva.`
    );
  }
  return order;
};

// General info del modal de la orden (GeneralInfo.vue) con todo lo de la
// cotización como está y lo que se cambia encima
export const generalInfoPayload = (order, changes = {}) => ({
  purchaseOrderNumber: order.purchaseOrderNumber,
  salesman: order.salesman,
  customerEmail: order.customerEmail,
  vin: order.vin,
  status: order.status,
  customerInfoautoId: order.customerInfoautoId,
  storeSource: order.storeSource,
  departamentoId: order.departamentoId,
  warehouseId: order.warehouseId,
  facturacionAutomatica: order.facturacionAutomatica,
  creditRetail: order.creditRetail,
  ...changes
});

// Totals del modal (Totals.vue): envío, pago, descuento, nivel de precio. El
// API reemplaza todos estos campos, así que van los de la cotización y lo que
// se cambia encima
export const totalsPayload = (order, changes = {}) => ({
  priceLevel: order.priceLevel,
  tax: order.tax,
  shippingPreference: order.shippingPreference,
  shippingPaid: order.shippingPaid,
  modalidadVentaId: order.modalidadVentaId,
  discount: order.discount,
  discountType: order.discountType || "fixed",
  discountPercentage: order.discountPercentage || 0,
  paymentType: order.paymentType,
  shippingSelectedServiceCarrier: order.shippingSelectedServiceCarrier,
  shippingSelectedServiceCode: order.shippingSelectedServiceCode,
  shippingSelectedServiceRate: order.shippingSelectedServiceRate,
  ...changes
});

const money = value =>
  value === null || value === undefined || value === "" ? null : Number(value);

// Lo que le falta a una cotización para estar completa y poder convertirse en
// orden sin volver a abrirla. La orden de compra, el VIN y las notas no son
// obligatorios.
const missingIn = (order, quote) => {
  const address = order.shippingAddress || {};
  return [
    !order.customerInfoautoId && !order.customerEmail && "cliente",
    !quote.added.length && "piezas",
    !(address.street1 && address.postalCode) && "dirección de envío",
    !order.shippingPreference && "envío (servicio y costo)",
    !order.paymentType && "tipo de pago",
    !order.creditRetail && "condición crédito/contado"
  ].filter(Boolean);
};

/**
 * La tarjeta y lo que lee Claude, con la cotización como quedó guardada: el
 * API puede recortar cantidades al stock (sus avisos van en warnings). Trae
 * todo lo que la hace completa: cliente, piezas, envío, pago, totales, para
 * que se vea qué falta.
 */
export const quoteResult = async (
  orderNumber,
  ctx,
  { skipped = [], warnings = [], notInQuote = [] } = {}
) => {
  const [order, paymentTypes] = await Promise.all([
    getOrder(orderNumber),
    getOrderPaymentTypes(ctx.collection).catch(() => [])
  ]);
  const lines = (order.items || []).map(item => ({
    nkrCode: item.nkrCode || item.kitCode,
    description: item.description,
    quantity: Number(item.quantity) || 0,
    price: Number(item.price) || 0,
    warehouse: item.warehouseCode || null
  }));
  const address = order.shippingAddress || null;
  const paymentType = paymentTypes.find(t => t.code === order.paymentType);
  const quote = {
    orderNumber,
    reference: quoteReference(orderNumber),
    name: order.quoteName || null,
    customerName:
      ((order.customerInfoauto || {}).generalInfo || {}).name ||
      order.customerEmail ||
      null,
    priceLevel: order.priceLevel || "online",
    added: lines,
    shipping: order.shippingPreference
      ? { service: order.shippingPreference, cost: money(order.shippingPaid) }
      : null,
    shippingAddress: address
      ? [
          address.name,
          address.street1,
          address.city,
          address.state,
          address.postalCode,
          address.country
        ]
          .filter(Boolean)
          .join(", ")
      : null,
    paymentType: order.paymentType
      ? paymentType
        ? paymentType.name
        : order.paymentType
      : null,
    // CRED / CONT: condición del cliente
    paymentTerms:
      order.creditRetail === "CRED"
        ? "Crédito"
        : order.creditRetail === "CONT"
        ? "Contado"
        : null,
    purchaseOrder: order.purchaseOrderNumber || null,
    vin: order.vin || null,
    internalNotes: order.internalNotes || null,
    customerNotes: order.customerNotes || null,
    totals: {
      products: money(order.productsTotal),
      discount: money(order.discount) || 0,
      shipping: money(order.shippingPaid) || 0,
      tax: money(order.tax) || 0,
      total: money(order.total)
    },
    skipped,
    warnings
  };
  quote.missing = missingIn(order, quote);
  // Para Claude lo mismo que la tarjeta, sin el número interno
  const details = { ...quote, lines: quote.added };
  delete details.orderNumber;
  delete details.added;
  return {
    card: { type: "quote", quote },
    content: JSON.stringify({
      shownToUserAsCard: true,
      ...details,
      ...(notInQuote.length ? { notInQuote } : {})
    })
  };
};
